import type { BabySex, Insurance, Mother } from '../types';
import { babies, mothers } from './mockData';
import { addAudit, type Actor } from './auditService';
import { respond } from './respond';
import { doc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { isReal, loadClinic } from './store';
import { scope } from './scope';

// Identity and insurance fields: clinic roles only, never sent to calls or WhatsApp.
const CLINIC_ROLES = ['facility_admin', 'doctor', 'staff'];
const guard = (actor?: Actor) => { if (!actor || !CLINIC_ROLES.includes(actor.role)) throw new Error('Not allowed'); };

export const ABHA_NUMBER_RE = /^\d{2}-\d{4}-\d{4}-\d{4}$/;
export const ABHA_ADDRESS_RE = /^[A-Za-z0-9][A-Za-z0-9._]{2,31}@abdm$/;

// Accepts 14 digits (formatted XX-XXXX-XXXX-XXXX) or name@abdm. Format check only, not verified.
export function normaliseAbha(raw: string): string | null {
  const v = raw.trim();
  if (!v) return '';
  const digits = v.replace(/[\s-]/g, '');
  if (/^\d{14}$/.test(digits)) return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 10)}-${digits.slice(10)}`;
  return ABHA_ADDRESS_RE.test(v) ? v.toLowerCase() : null;
}

export async function updateAbha(motherId: string, raw: string, actor?: Actor): Promise<Mother> {
  guard(actor);
  const abha = normaliseAbha(raw);
  if (abha === null) throw new Error('Invalid ABHA');
  if (isReal()) {
    const fid = scope().facilityId;
    await updateDoc(doc(db!, 'facilities', fid, 'mothers', motherId), { abha: abha || deleteField() });
    addAudit(actor, 'patient.abha_updated', `Patient ${motherId}`, fid, abha ? 'set' : 'cleared');
    return (await loadClinic(fid)).mothers.find((x) => x.id === motherId)!;
  }
  const m = mothers.find((x) => x.id === motherId)!;
  m.abha = abha || undefined;
  addAudit(actor, 'patient.abha_updated', `Patient ${motherId}`, m.facilityId, abha ? 'set' : 'cleared');
  return respond(m);
}

export async function updateInsurance(motherId: string, ins: Insurance, actor?: Actor): Promise<Mother> {
  guard(actor);
  const insurance: Insurance = ins.type === 'none' ? { type: 'none' } : { type: ins.type, insurer: ins.type === 'private' ? ins.insurer?.trim() || undefined : undefined, policyId: ins.policyId?.trim() || undefined };
  if (isReal()) {
    const fid = scope().facilityId;
    await updateDoc(doc(db!, 'facilities', fid, 'mothers', motherId), { insurance: JSON.parse(JSON.stringify(insurance)) });
    addAudit(actor, 'patient.insurance_updated', `Patient ${motherId}`, fid, ins.type);
    return (await loadClinic(fid)).mothers.find((x) => x.id === motherId)!;
  }
  const m = mothers.find((x) => x.id === motherId)!;
  m.insurance = insurance;
  addAudit(actor, 'patient.insurance_updated', `Patient ${motherId}`, m.facilityId, ins.type);
  return respond(m);
}

// Only exists once the baby is born; never available in pregnancy stage.
/** The baby's name: optional, after birth only. Blank removes it (the card then says "Baby of {mother}"). Display only. */
export async function setBabyName(motherId: string, raw: string, actor?: Actor): Promise<void> {
  guard(actor);
  const name = raw.trim().replace(/\s+/g, ' ').slice(0, 60);
  if (name && !/^[\p{L}\p{M} .'-]+$/u.test(name)) throw new Error('BAD_NAME');
  if (isReal()) {
    const fid = scope().facilityId;
    const c = await loadClinic(fid);
    const rm = c.mothers.find((x) => x.id === motherId);
    const rb = c.babies.find((x) => x.motherId === motherId);
    if (!rm || rm.stage !== 'baby' || !rb?.dob) throw new Error('Baby not born yet');
    await updateDoc(doc(db!, 'facilities', fid, 'babies', rb.id), { name: name || deleteField() });
    addAudit(actor, 'baby.name_updated', `Patient ${motherId}`, fid);
    return;
  }
  const m = mothers.find((x) => x.id === motherId);
  const b = babies.find((x) => x.motherId === motherId);
  if (!m || m.stage !== 'baby' || !b?.dob) throw new Error('Baby not born yet');
  if (name) b.name = name; else delete b.name;
  addAudit(actor, 'baby.name_updated', `Patient ${motherId}`, m.facilityId);
  return respond(undefined);
}

export async function setBabySex(motherId: string, sex: BabySex, actor?: Actor): Promise<void> {
  guard(actor);
  if (isReal()) {
    const fid = scope().facilityId;
    const c = await loadClinic(fid);
    const rm = c.mothers.find((x) => x.id === motherId);
    const rb = c.babies.find((x) => x.motherId === motherId);
    if (!rm || rm.stage !== 'baby' || !rb?.dob) throw new Error('Baby not born yet');
    await updateDoc(doc(db!, 'facilities', fid, 'babies', rb.id), { sex });
    addAudit(actor, 'baby.sex_updated', `Patient ${motherId}`, fid);
    return;
  }
  const m = mothers.find((x) => x.id === motherId);
  const b = babies.find((x) => x.motherId === motherId);
  if (!m || m.stage !== 'baby' || !b?.dob) throw new Error('Baby not born yet');
  b.sex = sex;
  addAudit(actor, 'baby.sex_updated', `Patient ${motherId}`, m.facilityId);
  return respond(undefined);
}
